import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getTeacherById, getAllSchedules } from '../services/api';
import { useNavigate, useLocation } from "react-router-dom";

const TeacherDetail = () => {
  const { id } = useParams();
  const [teacher, setTeacher] = useState(null);
  const [activeTab, setActiveTab] = useState('about');
  const [allSchedules, setAllSchedules] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToContact = () => {
    const teacherName = teacher.full_name || teacher.name;
    const subjectText = `Đăng ký học với ${teacherName}`;

    const teacherGrades = allSchedules
      .map(s => s.grade)
      .filter((grade, index, self) => self.indexOf(grade) === index); // Lọc trùng

    if (location.pathname !== "/") {
      navigate("/", { state: { scrollToContact: true, subjectText, teacherGrades } });
    } else {
      const el = document.getElementById("contact");
      if (el) el.scrollIntoView({ behavior: "smooth" });
      sessionStorage.setItem("subjectText", subjectText);
      sessionStorage.setItem("teacherGrades", JSON.stringify(teacherGrades));
    }
  };
  useEffect(() => {
    const fetchTeacherAndSchedule = async () => {
      try {
        const res = await getTeacherById(id);
        if (res && res.data) {
          const teacherData = res.data;

          teacherData.teaching_method = Array.isArray(teacherData.teaching_method)
            ? teacherData.teaching_method
            : typeof teacherData.teaching_method === 'string'
              ? teacherData.teaching_method.split(',').map(m => m.trim())
              : [];

          teacherData.awards = Array.isArray(teacherData.awards)
            ? teacherData.awards
            : typeof teacherData.awards === 'string'
              ? teacherData.awards.split(',').map(a => a.trim())
              : [];

          setTeacher(teacherData);

          // Sau khi set teacher -> lấy lịch dạy
          const scheduleRes = await getAllSchedules();
          if (scheduleRes && scheduleRes.data) {
            setAllSchedules(
              scheduleRes.data.filter(s => s.teacher_name === teacherData.full_name || s.teacher_name === teacherData.name)
            );
          }
        }
      } catch (error) {
        console.error('Error fetching teacher or schedule:', error);
      }
    };
    fetchTeacherAndSchedule();
  }, [id]);

  if (!teacher) {
    return <div>Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <div className="mb-6">
          <nav className="flex items-center space-x-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-blue-600">Trang chủ</Link>
            <span>/</span>
            <Link to="/giao-vien" className="hover:text-blue-600">Giáo viên</Link>
            <span>/</span>
            <span className="text-gray-900">{teacher.full_name || teacher.name}</span>
          </nav>
        </div>

        {/* Hero Section */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Left side - Teacher Info */}
            <div>
              <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  {teacher.title || 'Giáo viên'}
                </h1>
                <h2 className="text-3xl font-bold text-green-600">
                  {teacher.full_name || teacher.name}
                </h2>
              </div>

              {/* Info Card */}
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <div className="flex items-center mb-4">
                  <span className="text-blue-600 mr-2">🏢</span>
                  <span className="text-gray-600">Nơi công tác:</span>
                </div>
                <p className="font-semibold text-gray-900 mb-6">{teacher.workplace}</p>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-2xl mb-2">🎯</div>
                    <div className="text-sm text-gray-600 mb-1">Bộ Môn</div>
                    <div className="font-semibold text-blue-600">{teacher.major || teacher.subject}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl mb-2">💡</div>
                    <div className="text-sm text-gray-600 mb-1">SDT</div>
                    <div className="font-semibold text-blue-600">{teacher.phone_number}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl mb-2">🎓</div>
                    <div className="text-sm text-gray-600 mb-1">Trình Độ</div>
                    <div className="font-semibold text-blue-600">{teacher.degree}</div>
                  </div>
                </div>
              </div>

              {/* CTA Button */}
              <button
                onClick={scrollToContact}
                className="inline-block bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
              >
                Đăng ký học với {teacher.full_name || teacher.name}
              </button>

            </div>

            {/* Right side - Teacher Photo */}
            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                <div className="w-80 h-80 bg-orange-400 rounded-full overflow-hidden">
                  <div className="w-full h-full bg-gradient-to-br from-orange-300 to-orange-500 flex items-center justify-center">
                    <div className="w-72 h-72 bg-white rounded-full flex items-center justify-center text-6xl font-bold text-orange-500">
                      {teacher.full_name ? teacher.full_name.split(' ').map(n => n[0]).join('') : 'GV'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="bg-white rounded-lg shadow-sm mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-8">
              <button
                onClick={() => setActiveTab('about')}
                className={`py-4 px-2 border-b-2 font-medium text-sm ${activeTab === 'about'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
              >
                Đôi nét về giáo viên
              </button>
              <button
                onClick={() => setActiveTab('method')}
                className={`py-4 px-2 border-b-2 font-medium text-sm ${activeTab === 'method'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
              >
                Phương pháp giảng dạy
              </button>
              <button
                onClick={() => setActiveTab('achievements')}
                className={`py-4 px-2 border-b-2 font-medium text-sm ${activeTab === 'achievements'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
              >
                Thành tích nổi bật
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-8">
            {activeTab === 'about' && (
              <div>
                <div className="flex items-start mb-4">
                  <span className="text-orange-500 text-xl mr-3">⭐</span>
                  <p className="text-gray-700 leading-relaxed">
                    {teacher.note}
                  </p>
                </div>
              </div>
            )}

            {activeTab === 'method' && (
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Phương pháp giảng dạy đặc biệt</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {teacher.teaching_method && teacher.teaching_method.map((method, index) => (
                    <div key={index} className="bg-blue-50 rounded-lg p-6 text-center">
                      <div className="text-3xl mb-3">🎯</div>
                      <h4 className="font-semibold text-gray-900">{method}</h4>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'achievements' && (
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Các thành tích đạt được</h3>
                <div className="space-y-4">
                  {teacher.awards && teacher.awards.map((award, index) => (
                    <div key={index} className="flex items-center p-4 bg-green-50 rounded-lg">
                      <span className="text-green-600 text-xl mr-4">🏆</span>
                      <span className="text-gray-900 font-medium">{award}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Schedule Section */}
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">Lịch dạy trong tuần</h3>

          {allSchedules.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {allSchedules.map((item, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="font-semibold text-blue-600 mb-2">{item.schedule_day}</div>
                  <div className="text-gray-600 text-sm mb-1">{item.time_range}</div>
                  <div className="text-gray-900 font-medium">🎓 Lớp: {item.grade}</div>
                  <div className="text-gray-700 text-sm">📍 Phòng: {item.room}</div>
                  <div className="text-gray-700 text-sm">📚 Môn: {item.major}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-500">Không có lịch dạy nào.</div>
          )}
        </div>

      </div>
    </div>
  );
};

export default TeacherDetail;
